import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { SocialLoginButtons } from '@/components/auth';
import authApi, { type OAuthProvidersResponse } from '@/services/auth';
import { passkeyApi } from '@/services/passkey';
import { getDeviceFingerprint, getDeviceName } from '@/utils/fingerprint';
import { isWebAuthnSupported, WebAuthnError } from '@/utils/webauthn';
import { ApiClientError, EMAIL_NOT_VERIFIED_CODE, setAccessToken } from '@/services/api';
import { getSafeRedirect } from '@/utils/redirect';

/**
 * Extended OAuth providers response that includes email service status
 */
interface ProvidersStatus extends OAuthProvidersResponse {
  emailEnabled?: boolean;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error, clearError, isAuthenticated, setUser, mfaRequired, mfaToken } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [oauthProviders, setOauthProviders] = useState<ProvidersStatus | null>(null);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  // WP-SEC-27: the address that was refused because it is not verified yet
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  // Check if WebAuthn is supported
  const webAuthnSupported = isWebAuthnSupported();

  // Device fingerprint for device tracking
  // Track both the fingerprint value and whether it's ready
  const deviceFingerprintRef = useRef<string | null>(null);
  const deviceNameRef = useRef<string | null>(null);
  const [fingerprintReady, setFingerprintReady] = useState(false);

  // Initialize device fingerprint on mount
  // This is important for MFA device trust - the fingerprint MUST be available
  // when the user clicks OAuth buttons, otherwise device trust cannot be established
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        deviceFingerprintRef.current = await getDeviceFingerprint();
        deviceNameRef.current = getDeviceName();
        setFingerprintReady(true);
      } catch {
        // Fingerprint collection failed - still mark as ready to not block login
        // Device trust just won't work in this case
        setFingerprintReady(true);
      }
    };
    initFingerprint();
  }, []);

  // Fetch OAuth providers to conditionally show the "Or continue with" section
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await authApi.getOAuthProviders();
        setOauthProviders(response);
      } catch {
        // If we can't fetch providers, assume none are available
        setOauthProviders({ googleEnabled: false, appleEnabled: false, microsoftEnabled: false, emailEnabled: false });
      } finally {
        setProvidersLoading(false);
      }
    };
    fetchProviders();
  }, []);

  // Check maintenance status and poll during maintenance mode
  // This allows the login page to show an "upgrading" banner during deployments
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const { maintenance } = await authApi.getMaintenanceStatus();
        setMaintenanceActive(maintenance);
      } catch {
        // If we can't fetch status, assume not in maintenance
        setMaintenanceActive(false);
      }
    };

    // Initial check
    checkMaintenance();

    // Poll every 10 seconds to detect when maintenance ends
    const interval = setInterval(checkMaintenance, 10000);
    return () => clearInterval(interval);
  }, []);

  // Check if email service is available for magic link
  const isEmailEnabled = oauthProviders?.emailEnabled === true;

  // Get the redirect destination from query params or location state
  // Note: redirectParam is validated to prevent open redirect attacks
  const redirectParam = searchParams.get('redirect');
  const stateFrom = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const from = getSafeRedirect(redirectParam) ?? stateFrom ?? '/dashboard';

  // Check for success messages from other flows
  const emailVerified = searchParams.get('email_verified') === 'true';
  const passwordReset = searchParams.get('password_reset') === 'true';

  // WP-SESSION-3: Check for session expired message from client-side auto-logout
  const sessionExpired = (location.state as { sessionExpired?: boolean })?.sessionExpired === true;

  // WP-SESSION-4: Check for server-side idle timeout (query param from api.ts 419 handler)
  const idleTimeout = searchParams.get('sessionExpired') === 'idle';

  // WP-SEC-27: signed out because the account's email is not verified (api.ts 403 handler)
  const emailNotVerifiedRedirect = searchParams.get('emailNotVerified') === '1';

  // Navigate to dashboard when authenticated (handles OAuth success)
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // WP-AUTH-7: Navigate to MFA page when OAuth/social login requires MFA
  useEffect(() => {
    if (mfaRequired && mfaToken) {
      navigate('/mfa', {
        state: {
          mfaToken,
          from: { pathname: from },
          deviceFingerprint: deviceFingerprintRef.current,
        },
        replace: true,
      });
    }
  }, [mfaRequired, mfaToken, navigate, from]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setUnverifiedEmail(null);
    setResendState('idle');

    try {
      // Include device fingerprint and name in login request
      const result = await login(
        email,
        password,
        deviceFingerprintRef.current || undefined,
        deviceNameRef.current || undefined
      );

      if (result.requiresMfa && result.mfaToken) {
        // Redirect to MFA challenge page with token and fingerprint
        navigate('/mfa', {
          state: {
            mfaToken: result.mfaToken,
            from: { pathname: from },
            deviceFingerprint: deviceFingerprintRef.current,
          },
          replace: true,
        });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      // The error text is handled by the store. WP-SEC-27: right password,
      // but the address is not verified yet - offer to resend the link.
      if (err instanceof ApiClientError && err.code === EMAIL_NOT_VERIFIED_CODE) {
        setUnverifiedEmail(email);
      }
    }
  };

  // WP-SEC-27: resend the verification link. The answer is the same whether
  // or not the address has an account (no enumeration).
  const handleResendVerification = async () => {
    const address = unverifiedEmail || email;
    if (!address) {
      return;
    }
    setResendState('sending');
    try {
      await authApi.resendVerificationEmail(address);
      setResendState('sent');
    } catch {
      setResendState('failed');
    }
  };

  // Handle passkey login
  const handlePasskeyLogin = async () => {
    setPasskeyError(null);
    setPasskeyLoading(true);
    clearError();

    try {
      const response = await passkeyApi.authenticateWithPasskey();

      // WP-AUTH-12: Only set access token - refresh token is managed via httpOnly cookie
      if (response.tokens) {
        setAccessToken(response.tokens.accessToken);
      }
      if (response.user) {
        setUser(response.user);
      }

      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof WebAuthnError) {
        if (err.type === 'cancelled') {
          // User cancelled, don't show error
          setPasskeyError(null);
        } else {
          setPasskeyError(err.message);
        }
      } else if (err instanceof Error) {
        setPasskeyError(err.message);
      } else {
        setPasskeyError('Failed to sign in with passkey. Please try again.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
        Sign in to your account
      </h2>

      {/* System maintenance banner - shown during deployments */}
      {maintenanceActive && (
        <Alert variant="warning" title="System Upgrade in Progress" className="mb-4">
          We're upgrading FlowIO. Please wait a moment and try again.
        </Alert>
      )}

      {/* Session expired message - WP-SESSION-3 (client-side) */}
      {sessionExpired && !idleTimeout && (
        <Alert variant="info" className="mb-4">
          Your session expired due to inactivity. Please sign in again.
        </Alert>
      )}

      {/* Server-side idle timeout message - WP-SESSION-4 */}
      {idleTimeout && (
        <Alert variant="warning" className="mb-4">
          Your session was ended by the server due to inactivity. Please sign in again.
        </Alert>
      )}

      {/* Success messages from other flows */}
      {emailVerified && (
        <Alert variant="success" className="mb-4">
          Your email has been verified. You can now sign in.
        </Alert>
      )}
      {passwordReset && (
        <Alert variant="success" className="mb-4">
          Your password has been reset. You can now sign in with your new password.
        </Alert>
      )}

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={clearError}>
          {error}
        </Alert>
      )}

      {/* WP-SEC-27: the account's email is not verified yet */}
      {emailNotVerifiedRedirect && !unverifiedEmail && (
        <Alert variant="info" className="mb-4">
          Check your email to verify your account. Enter your email below to get a new link.
        </Alert>
      )}
      {(unverifiedEmail || emailNotVerifiedRedirect) && (
        <div className="mb-4">
          {resendState === 'sent' ? (
            <Alert variant="success">
              If this account still needs verifying, a new link is on its way. Check your inbox.
            </Alert>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              isLoading={resendState === 'sending'}
              disabled={!(unverifiedEmail || email)}
              onClick={handleResendVerification}
            >
              Resend verification email
            </Button>
          )}
          {resendState === 'failed' && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              We couldn&apos;t send the email. Please try again in a few minutes.
            </p>
          )}
        </div>
      )}
      {passkeyError && (
        <Alert variant="error" className="mb-4" onDismiss={() => setPasskeyError(null)}>
          {passkeyError}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          autoFocus
          required
        />

        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          autoComplete="current-password"
          required
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-600">Remember me</span>
          </label>

          <Link
            to="/forgot-password"
            className="text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Sign in
        </Button>
      </form>

      {/* Alternative login options section - only show after providers have loaded */}
      {!providersLoading && (
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {/* Passkey option - only shown when WebAuthn is supported */}
            {webAuthnSupported && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={isLoading || passkeyLoading}
                isLoading={passkeyLoading}
                onClick={handlePasskeyLogin}
                leftIcon={
                  !passkeyLoading ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>
                  ) : undefined
                }
              >
                {passkeyLoading ? 'Signing in...' : 'Sign in with passkey'}
              </Button>
            )}

            {/* Magic link option - only shown when email service is enabled */}
            {isEmailEnabled && (
              <Link to="/magic-link" className="block">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={isLoading || passkeyLoading}
                  leftIcon={
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  }
                >
                  Sign in with magic link
                </Button>
              </Link>
            )}

            {/* Social login buttons - only shown when providers are configured */}
            {/* WP-AUTH-7: Pass device fingerprint for MFA device trust check */}
            {/* Disable OAuth buttons until fingerprint is ready to ensure device trust works */}
            <SocialLoginButtons
              disabled={isLoading || passkeyLoading || !fingerprintReady}
              deviceFingerprint={deviceFingerprintRef.current || undefined}
            />
          </div>
        </div>
      )}

      <p className="mt-6 text-center text-sm text-gray-600">
        Don&apos;t have an account?{' '}
        <Link
          to="/register"
          className="font-medium text-primary-600 hover:text-primary-500"
        >
          Sign up for free
        </Link>
      </p>
    </div>
  );
}
